"use client";

import { RefreshCw, WifiOff } from "lucide-react";

import { Button } from "@/components/ui/button";

/**
 * The app's error boundary.
 *
 * `src/lib/session.ts` has always deliberately re-thrown infrastructure faults — "an
 * infrastructure fault is NOT a signed-out state ... let it propagate to the error boundary
 * instead" — but no boundary existed, so those faults reached staff as Next's default error
 * screen. This is the boundary that comment assumed.
 *
 * 🔴 **It must never read as "you are signed out" or as a verdict about a member.** Bouncing
 * staff to the login screen during a backend outage teaches them to re-enter credentials when
 * something breaks, which is exactly the habit a phishing attempt needs. So: no sign-out
 * suggestion, no "access denied" wording, and Retry stays on this page.
 */
export default function ErrorBoundary({ reset }: { error: Error; reset: () => void }) {
  return (
    <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center gap-6 px-5 py-10 text-center">
      <WifiOff className="size-14 text-muted-foreground" aria-hidden />
      <div className="space-y-2">
        <h1 className="text-xl font-bold">Can&apos;t reach XFit</h1>
        <p className="text-sm text-muted-foreground">
          You are still signed in. This is a connection or server problem, not a problem with
          your account or with the member in front of you.
        </p>
      </div>
      {/*
        The error message itself is deliberately not rendered. It can carry backend internals,
        and a front-desk screen is visible to whoever is standing at the counter. Next logs the
        real error server-side, where it belongs.
      */}
      <Button onClick={reset} size="lg">
        <RefreshCw className="size-5" aria-hidden />
        Try again
      </Button>
    </main>
  );
}
